import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fxAdminApi, FxOrderListItem, FxOrderDetail, FxQuoteResponse, FxQuotePreviewResponse, IndicativeDailyRate, FxMarkupRule } from '../api/fx';
import { getErrorMessage } from '../api/client';
import { providersApi, Provider } from '../api/providers';
import { adminApi, Client, ClientAccountListItem, CurrencyDto } from '../api/admin';
import { useSelectedClientStore } from '../store/selectedClientStore';
import { getClientDisplayName, getClientOptionLabel } from '../utils/clientDisplay';
import EnglishDatePicker from '../components/common/EnglishDatePicker';

type Tab = 'orders' | 'rates' | 'markup';

const STATUS_COLORS: Record<string, string> = {
  'Created': 'bg-blue-100 text-blue-700',
  'Awaiting Execution': 'bg-yellow-100 text-yellow-700',
  'Awaiting Pyvio Confirmation': 'bg-indigo-100 text-indigo-700',
  'Executed': 'bg-green-100 text-green-700',
  'Failed': 'bg-red-100 text-red-700',
  'Cancelled': 'bg-gray-100 text-gray-600',
  'Rejected': 'bg-orange-100 text-orange-700',
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const todayIsoDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Prefer real dates; skip Invalid Date / year-1 placeholders from API. */
const formatFxDate = (
  ...candidates: Array<string | null | undefined>
): string => {
  for (const raw of candidates) {
    if (!raw) continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1970) continue;
    return d.toLocaleDateString('en-GB');
  }
  return '—';
};

const formatFxDateTime = (
  ...candidates: Array<string | null | undefined>
): string => {
  for (const raw of candidates) {
    if (!raw) continue;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1970) continue;
    return d.toLocaleString('en-GB');
  }
  return '—';
};

const resolveCurrencyCode = (
  raw: string | null | undefined,
  currencyCodeById: Map<string, string>
): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const byId = currencyCodeById.get(trimmed.toUpperCase());
  if (byId) return byId;

  if (UUID_REGEX.test(trimmed)) return null;
  return trimmed.toUpperCase();
};

// ─── Orders Tab ───────────────────────────────────────────────────────────────
function OrdersTab() {
  const { selectedClient } = useSelectedClientStore();
  const [orders, setOrders] = useState<FxOrderListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<FxOrderDetail | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Execute form state
  const [showExecuteForm, setShowExecuteForm] = useState(false);
  const [execProviderRate, setExecProviderRate] = useState('');
  const [execClientRate, setExecClientRate] = useState('');
  const [execTradeId, setExecTradeId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [quote, setQuote] = useState<FxQuoteResponse | null>(null);
  const [livePreview, setLivePreview] = useState<FxQuotePreviewResponse | null>(null);
  const [availableMarkupRules, setAvailableMarkupRules] = useState<FxMarkupRule[]>([]);
  const [loadingMarkupRules, setLoadingMarkupRules] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientAccounts, setClientAccounts] = useState<ClientAccountListItem[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyDto[]>([]);
  const [createForm, setCreateForm] = useState({
    clientId: selectedClient?.id ?? '',
    sourceClientAccountId: '',
    targetClientAccountId: '',
    amount: '',
    amountSide: 'from' as 'from' | 'to',
    paymentDate: todayIsoDate(),
    useCustomRates: false,
    useFixedMarkupUsd: false,
    fixedMarkupAmount: '',
    customProviderRate: '',
    customClientRate: '',
    selectedMarkupRuleId: '',
  });

  // Sync clientId with selectedClient when modal opens
  useEffect(() => {
    if (showCreateModal && selectedClient?.id) {
      setCreateForm((prev) => ({
        ...prev,
        clientId: selectedClient.id,
      }));
    }
  }, [showCreateModal, selectedClient?.id]);

  // Keep FX client context aligned with global selected client (same behavior as Payments page).
  useEffect(() => {
    const scopedClientId = selectedClient?.id ?? '';
    setCreateForm((prev) => {
      if (prev.clientId === scopedClientId) return prev;
      return {
        ...prev,
        clientId: scopedClientId,
        sourceClientAccountId: '',
        targetClientAccountId: '',
        useCustomRates: false,
        useFixedMarkupUsd: false,
        fixedMarkupAmount: '',
        customProviderRate: '',
        customClientRate: '',
        selectedMarkupRuleId: '',
      };
    });
    setQuote(null);
    setLivePreview(null);
    setActionError('');
  }, [selectedClient?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fxAdminApi.getOrders({
        page,
        pageSize: 20,
        status: statusFilter || undefined,
        clientId: selectedClient?.id || undefined,
      });
      setOrders(res.data.items);
      setTotal(res.data.totalCount);
    } catch { }
    setLoading(false);
  }, [page, statusFilter, selectedClient?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminApi.getClients().then((r) => setClients(r.data || [])).catch(() => {});
    adminApi.getCurrencies().then((r) => setCurrencies(r.data || [])).catch(() => {});
  }, []);

  const currencyCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of currencies) {
      if (!c?.id || !c?.code) continue;
      map.set(c.id.toUpperCase(), c.code.toUpperCase());
      map.set(c.code.toUpperCase(), c.code.toUpperCase());
    }
    return map;
  }, [currencies]);

  const resolveAccountCurrency = useCallback((raw: string | null | undefined) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    if (!trimmed) return '';
    const byKey = currencyCodeById.get(trimmed.toUpperCase());
    if (byKey) return byKey;
    return UUID_REGEX.test(trimmed) ? '' : trimmed.toUpperCase();
  }, [currencyCodeById]);

  const formatFxAccountOption = useCallback((a: ClientAccountListItem) => {
    const ccy = resolveAccountCurrency(a.currencyId) || a.currencyId || '—';
    const provider = (a.providerName || '').trim() || 'No provider';
    const bal = Number(a.availableBalance ?? 0).toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const rawNum = (a.accountNumber || a.iban || '').trim();
    const shortNum = rawNum.length > 10 ? `…${rawNum.slice(-6)}` : rawNum;
    return shortNum
      ? `${ccy} · ${provider} · ${bal} · ${shortNum}`
      : `${ccy} · ${provider} · ${bal}`;
  }, [resolveAccountCurrency]);
  const displayFxCurrency = useCallback((raw: string | null | undefined) => {
    if (!raw) return '';
    return resolveCurrencyCode(raw, currencyCodeById) ?? raw;
  }, [currencyCodeById]);

  useEffect(() => {
    if (!createForm.clientId) {
      setClientAccounts([]);
      setQuote(null);
      setAvailableMarkupRules([]);
      return;
    }
    adminApi.getClientAccountsByClientId(createForm.clientId)
      .then((r) => setClientAccounts(r.data || []))
      .catch(() => setClientAccounts([]));
  }, [createForm.clientId]);

  const selectedSourceAccount = clientAccounts.find((a) => a.id === createForm.sourceClientAccountId);
  const selectedTargetAccount = clientAccounts.find((a) => a.id === createForm.targetClientAccountId);
  const activeClientAccounts = useMemo(
    () => clientAccounts
      .filter((a) => {
        const status = (a.status || '').trim().toUpperCase();
        return status === 'ACTIVE' || status === 'OPEN';
      })
      .slice()
      .sort((a, b) => {
        const ccy = resolveAccountCurrency(a.currencyId).localeCompare(resolveAccountCurrency(b.currencyId));
        if (ccy !== 0) return ccy;
        return Number(b.availableBalance ?? 0) - Number(a.availableBalance ?? 0);
      }),
    [clientAccounts, resolveAccountCurrency]
  );
  const compatibleTargetAccounts = useMemo(() => {
    if (!selectedSourceAccount) return activeClientAccounts.filter((a) => a.id !== createForm.sourceClientAccountId);
    const sourceCcy = resolveAccountCurrency(selectedSourceAccount.currencyId);
    return activeClientAccounts.filter((a) =>
      a.id !== selectedSourceAccount.id
      && a.providerId === selectedSourceAccount.providerId
      && resolveAccountCurrency(a.currencyId) !== sourceCcy
    );
  }, [activeClientAccounts, selectedSourceAccount, createForm.sourceClientAccountId, resolveAccountCurrency]);
  const sourceProviderId = selectedSourceAccount?.providerId;
  const sourceCurrency = selectedSourceAccount ? resolveAccountCurrency(selectedSourceAccount.currencyId) : undefined;
  const targetCurrency = selectedTargetAccount ? resolveAccountCurrency(selectedTargetAccount.currencyId) : undefined;
  const selectedMarkupRule = createForm.selectedMarkupRuleId
    ? availableMarkupRules.find((r) => r.id === createForm.selectedMarkupRuleId)
    : null;
  const selectedMarkupRuleType = (selectedMarkupRule?.markupType || '').trim().toUpperCase();
  const isFixedMarkupMode = createForm.useFixedMarkupUsd || selectedMarkupRuleType === 'FIXED';
  const fixedMarkupAmountFromInput = Number(createForm.fixedMarkupAmount);
  const fixedMarkupAmount = createForm.useFixedMarkupUsd
    ? (Number.isFinite(fixedMarkupAmountFromInput) ? fixedMarkupAmountFromInput : undefined)
    : (selectedMarkupRuleType === 'FIXED' && Number.isFinite(selectedMarkupRule?.markupValue)
      ? Number(selectedMarkupRule?.markupValue)
      : undefined);
  const markupSelectValue = createForm.useFixedMarkupUsd
    ? '__FIXED_USD__'
    : (createForm.useCustomRates ? '__CUSTOM__' : createForm.selectedMarkupRuleId);
  const sourceCurrencyLabel = sourceCurrency || 'USD';
  const allowProviderRateInput = createForm.useCustomRates || createForm.useFixedMarkupUsd;

  useEffect(() => {
    if (!showCreateModal) return;

    const amount = Number(createForm.amount);
    if (!createForm.sourceClientAccountId || !createForm.targetClientAccountId) {
      setLivePreview(null);
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setLivePreview(null);
      return;
    }

    const customProviderRate = allowProviderRateInput && createForm.customProviderRate
      ? Number(createForm.customProviderRate)
      : undefined;
    const customClientRate = createForm.useCustomRates && !isFixedMarkupMode && createForm.customClientRate
      ? Number(createForm.customClientRate)
      : undefined;
    if (customProviderRate !== undefined && (!Number.isFinite(customProviderRate) || customProviderRate <= 0)) {
      setLivePreview(null);
      return;
    }
    if (customClientRate !== undefined && (!Number.isFinite(customClientRate) || customClientRate <= 0)) {
      setLivePreview(null);
      return;
    }

    const useFixedMarkupAmount = isFixedMarkupMode && fixedMarkupAmount !== undefined;
    const selectedMarkupRuleId = useFixedMarkupAmount
      ? undefined
      : (createForm.selectedMarkupRuleId || undefined);

    const timer = window.setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await fxAdminApi.previewOrder({
          sourceClientAccountId: createForm.sourceClientAccountId,
          targetClientAccountId: createForm.targetClientAccountId,
          amount,
          amountSide: createForm.amountSide,
          customProviderRate,
          customClientRate,
          fixedMarkupAmount: useFixedMarkupAmount ? fixedMarkupAmount : undefined,
          selectedMarkupRuleId,
        });
        setLivePreview(res.data);
        setQuote(null);
        setActionError('');
      } catch (e) {
        setLivePreview(null);
        setActionError(getErrorMessage(e));
      } finally {
        setPreviewLoading(false);
      }
    }, 600);

    return () => window.clearTimeout(timer);
  }, [
    showCreateModal,
    createForm.sourceClientAccountId,
    createForm.targetClientAccountId,
    createForm.amount,
    createForm.amountSide,
    createForm.customProviderRate,
    createForm.customClientRate,
    createForm.useCustomRates,
    createForm.useFixedMarkupUsd,
    createForm.fixedMarkupAmount,
    createForm.selectedMarkupRuleId,
    allowProviderRateInput,
    isFixedMarkupMode,
    fixedMarkupAmount,
  ]);

  const appliedMarkupRule = quote?.appliedMarkupRuleId
    ? availableMarkupRules.find((r) => r.id === quote.appliedMarkupRuleId)
    : null;
  const clientDisplayById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients) {
      if (!c?.id) continue;
      const displayName = getClientDisplayName(c) || c.id.slice(0, 8);
      map.set(c.id.toLowerCase(), displayName);
    }
    return map;
  }, [clients]);
  const quoteFromCurrency = quote
    ? (resolveCurrencyCode(quote.fromCurrency, currencyCodeById) ?? quote.fromCurrency)
    : '';
  const quoteToCurrency = quote
    ? (resolveCurrencyCode(quote.toCurrency, currencyCodeById) ?? quote.toCurrency)
    : '';
  const quoteProviderSourceAmount = quote?.providerSourceAmount ?? quote?.fromAmount ?? 0;
  const quoteIsFixedMarkup = (quote?.markupType || '').toUpperCase() === 'FIXED_AMOUNT';
  const formatRate = (value?: number | null) =>
    typeof value === 'number' && Number.isFinite(value) ? value.toFixed(10) : '—';
  const formatRateInput = (value?: number | null) =>
    typeof value === 'number' && Number.isFinite(value) ? value.toFixed(10) : '';
  const quoteMarkupLabel = quote
    ? (quote.markupType === 'PERCENTAGE'
      ? `${quote.markupValue}%`
      : `${quote.markupValue} ${quoteFromCurrency}`)
    : '';
  const quoteRateSourceLabel = quote?.rateSource === 'PYVIO'
    ? 'Pyvio live'
    : quote?.rateSource === 'MANUAL'
      ? 'Manual'
      : 'Internal tariff';
  const quoteProviderMarkupLabel = quote
    ? (quoteIsFixedMarkup
      ? `${quoteRateSourceLabel} · ${quote.providerName || '—'} · ${formatRate(quote.providerRate)} + ${quote.markupValue.toFixed(2)} ${quoteFromCurrency}`
      : `${quoteRateSourceLabel} · ${quote.providerName || '—'} · ${formatRate(quote.providerRate)} · ${quoteMarkupLabel}`)
    : '—';

  useEffect(() => {
    if (!sourceProviderId || !sourceCurrency || !targetCurrency) {
      setAvailableMarkupRules([]);
      return;
    }

    setLoadingMarkupRules(true);
    fxAdminApi.getMarkupRules(sourceProviderId)
      .then((res) => {
        const now = Date.now();
        const source = sourceCurrency.toUpperCase();
        const target = targetCurrency.toUpperCase();
        const amount = Number(createForm.amount);
        const hasAmount = Number.isFinite(amount) && amount > 0;

        const filtered = (res.data || []).filter((r) => {
          if (!r.isActive) return false;
          const fromTime = new Date(r.effectiveFrom).getTime();
          const toTime = r.effectiveTo ? new Date(r.effectiveTo).getTime() : Number.POSITIVE_INFINITY;
          if (Number.isNaN(fromTime) || now < fromTime || now > toTime) return false;

          const ruleSource = (r.sourceCurrency || '').toUpperCase();
          const ruleTarget = (r.targetCurrency || '').toUpperCase();
          const wildcard = !ruleSource && !ruleTarget;
          const exact = ruleSource === source && ruleTarget === target;
          if (!(wildcard || exact)) return false;

          if (!hasAmount) return true;
          const minOk = r.minAmount == null || amount >= r.minAmount;
          const maxOk = r.maxAmount == null || amount <= r.maxAmount;
          return minOk && maxOk;
        });

        setAvailableMarkupRules(filtered);
      })
      .catch(() => setAvailableMarkupRules([]))
      .finally(() => setLoadingMarkupRules(false));
  }, [sourceProviderId, sourceCurrency, targetCurrency, createForm.amount]);

  const openOrder = async (id: string) => {
    const res = await fxAdminApi.getOrder(id);
    const order = res.data;
    setSelected(order);
    setActionError('');
    setExecTradeId('');
    if (order.status === 'Awaiting Execution') {
      setExecProviderRate(formatRateInput(order.providerRate));
      setExecClientRate(formatRateInput(order.clientRate));
      setShowExecuteForm(true);
    } else {
      setShowExecuteForm(false);
      setExecProviderRate('');
      setExecClientRate('');
    }
  };

  const openExecuteForm = () => {
    if (!selected) return;
    setExecProviderRate(formatRateInput(selected.providerRate));
    setExecClientRate(formatRateInput(selected.clientRate));
    setActionError('');
    setShowExecuteForm(true);
  };

  const handleExecuteSubmit = async () => {
    if (!selected) return;
    const providerRateVal = Number(execProviderRate);
    if (!execProviderRate.trim() || !Number.isFinite(providerRateVal) || providerRateVal <= 0) {
      setActionError('Real Provider Rate is required and must be a positive number.');
      return;
    }
    const clientRateVal = execClientRate.trim() ? Number(execClientRate) : undefined;
    if (execClientRate.trim() && (!Number.isFinite(clientRateVal!) || clientRateVal! <= 0)) {
      setActionError('Client Rate must be a positive number if provided.');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await fxAdminApi.executeOrder({
        transactionId: selected.id,
        providerRate: providerRateVal,
        clientRate: clientRateVal,
        providerTradeId: execTradeId || undefined,
      });
      setSelected(null);
      setShowExecuteForm(false);
      load();
    } catch (e) { setActionError(getErrorMessage(e)); }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!selected) return;
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    setActionLoading(true);
    setActionError('');
    try {
      await fxAdminApi.rejectOrder({ transactionId: selected.id, reason });
      setSelected(null);
      load();
    } catch (e) { setActionError(getErrorMessage(e)); }
    setActionLoading(false);
  };

  const handleGetQuote = async () => {
    const amount = Number(createForm.amount);
    const customProviderRate = allowProviderRateInput && createForm.customProviderRate
      ? Number(createForm.customProviderRate)
      : undefined;
    const customClientRate = createForm.useCustomRates && !isFixedMarkupMode && createForm.customClientRate
      ? Number(createForm.customClientRate)
      : undefined;
    if (!createForm.clientId || !createForm.sourceClientAccountId || !createForm.targetClientAccountId) {
      setActionError('Select client, source and target accounts.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setActionError('Amount must be a positive number.');
      return;
    }
    if (createForm.sourceClientAccountId === createForm.targetClientAccountId) {
      setActionError('Source and target accounts must be different.');
      return;
    }
    if (customProviderRate !== undefined && (!Number.isFinite(customProviderRate) || customProviderRate <= 0)) {
      setActionError('Custom provider rate must be a positive number.');
      return;
    }
    if (customClientRate !== undefined && (!Number.isFinite(customClientRate) || customClientRate <= 0)) {
      setActionError('Custom client rate must be a positive number.');
      return;
    }
    if (isFixedMarkupMode && customClientRate !== undefined) {
      setActionError('Custom client rate is not allowed for Fixed markup in USD.');
      return;
    }
    if (isFixedMarkupMode && (fixedMarkupAmount === undefined || fixedMarkupAmount <= 0)) {
      setActionError('Markup amount (USD) must be a positive number.');
      return;
    }
    if (isFixedMarkupMode && createForm.amountSide === 'from' && fixedMarkupAmount !== undefined && fixedMarkupAmount >= amount) {
      setActionError('Markup amount must be less than the source amount.');
      return;
    }

    const useFixedMarkupAmount = isFixedMarkupMode && fixedMarkupAmount !== undefined;
    const selectedMarkupRuleId = useFixedMarkupAmount
      ? undefined
      : (createForm.selectedMarkupRuleId || undefined);

    setQuoteLoading(true);
    setActionError('');
    try {
      const res = await fxAdminApi.quoteOrder({
        sourceClientAccountId: createForm.sourceClientAccountId,
        targetClientAccountId: createForm.targetClientAccountId,
        amount,
        amountSide: createForm.amountSide,
        customProviderRate,
        customClientRate,
        fixedMarkupAmount: useFixedMarkupAmount ? fixedMarkupAmount : undefined,
        selectedMarkupRuleId,
      });
      setQuote(res.data);
    } catch (e) {
      setQuote(null);
      setActionError(getErrorMessage(e));
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    const amount = Number(createForm.amount);
    const customProviderRate = allowProviderRateInput && createForm.customProviderRate
      ? Number(createForm.customProviderRate)
      : undefined;
    const customClientRate = createForm.useCustomRates && !isFixedMarkupMode && createForm.customClientRate
      ? Number(createForm.customClientRate)
      : undefined;
    const quoteTransactionId = quote?.transactionId
      || (quote as { TransactionId?: string } | null)?.TransactionId;
    if (!quote || !quoteTransactionId) {
      setActionError('Get quote first.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setActionError('Amount must be a positive number.');
      return;
    }
    if (customProviderRate !== undefined && (!Number.isFinite(customProviderRate) || customProviderRate <= 0)) {
      setActionError('Custom provider rate must be a positive number.');
      return;
    }
    if (customClientRate !== undefined && (!Number.isFinite(customClientRate) || customClientRate <= 0)) {
      setActionError('Custom client rate must be a positive number.');
      return;
    }
    if (isFixedMarkupMode && customClientRate !== undefined) {
      setActionError('Custom client rate is not allowed for Fixed markup in USD.');
      return;
    }
    if (isFixedMarkupMode && (fixedMarkupAmount === undefined || fixedMarkupAmount <= 0)) {
      setActionError('Markup amount (USD) must be a positive number.');
      return;
    }

    const useFixedMarkupAmount = isFixedMarkupMode && fixedMarkupAmount !== undefined;
    const selectedMarkupRuleId = useFixedMarkupAmount
      ? undefined
      : (createForm.selectedMarkupRuleId || undefined);

    setCreateLoading(true);
    setActionError('');
    try {
      await fxAdminApi.createOrder({
        transactionId: quoteTransactionId,
        sourceClientAccountId: createForm.sourceClientAccountId,
        targetClientAccountId: createForm.targetClientAccountId,
        amount,
        amountSide: createForm.amountSide,
        customProviderRate,
        customClientRate,
        fixedMarkupAmount: useFixedMarkupAmount ? fixedMarkupAmount : undefined,
        selectedMarkupRuleId,
        paymentDate: createForm.paymentDate || undefined,
      });
      setShowCreateModal(false);
      setQuote(null);
      setCreateForm({
        clientId: selectedClient?.id ?? '',
        sourceClientAccountId: '',
        targetClientAccountId: '',
        amount: '',
        amountSide: 'from',
        paymentDate: todayIsoDate(),
        useCustomRates: false,
        useFixedMarkupUsd: false,
        fixedMarkupAmount: '',
        customProviderRate: '',
        customClientRate: '',
        selectedMarkupRuleId: '',
      });
      await load();
    } catch (e) {
      setActionError(getErrorMessage(e));
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => {
            setShowCreateModal(true);
            setActionError('');
            setQuote(null);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          + Create FX order
        </button>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All statuses</option>
          <option value="Created">Created</option>
          <option value="AwaitingExecution">Awaiting Execution</option>
          <option value="ProviderPending">Awaiting Pyvio Confirmation</option>
          <option value="Executed">Executed</option>
          <option value="Failed">Failed</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Rejected">Rejected</option>
        </select>
        <span className="text-sm text-gray-500">{total} total</span>
        {selectedClient?.id && (
          <span className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-full">
            Client scope: {getClientDisplayName(selectedClient) || selectedClient.fullName || selectedClient.email || selectedClient.id.slice(0, 8)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Client', 'From', 'To', 'Client rate', 'Profit', 'Status', 'Created', 'Payment date', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-700">
                    {(() => {
                      const apiClientName = (o.clientName || '').trim();
                      if (apiClientName) return apiClientName;
                      if (o.userId) {
                        const mappedName = clientDisplayById.get(o.userId.toLowerCase());
                        if (mappedName) return mappedName;
                      }
                      return o.userId?.slice(0, 8) || o.id.slice(0, 8);
                    })()}
                  </td>
                  <td className="px-4 py-3 font-medium">{o.fromAmount.toLocaleString('en-GB')} {displayFxCurrency(o.fromCurrency)}</td>
                  <td className="px-4 py-3 font-medium">{o.toAmount.toLocaleString('en-GB')} {displayFxCurrency(o.toCurrency)}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{formatRate(o.clientRate)}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {o.status === 'Executed' && o.profitAmount != null && o.profitAmount > 0
                      ? `${o.profitAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${displayFxCurrency(o.profitCurrency || o.fromCurrency)}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-600'}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {formatFxDateTime(o.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {formatFxDate(o.paymentDate, o.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => openOrder(o.id)} className="text-blue-600 hover:underline text-xs">View</button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex gap-2 mt-4 justify-end">
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
          className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">Prev</button>
        <span className="px-3 py-1.5 text-sm text-gray-600">Page {page}</span>
        <button disabled={orders.length < 20} onClick={() => setPage(p => p + 1)}
          className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Create FX conversion</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setActionError('');
                  setQuote(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Client</label>
                  <select
                    value={createForm.clientId}
                    onChange={(e) => setCreateForm((f) => ({
                      ...f,
                      clientId: e.target.value,
                      sourceClientAccountId: '',
                      targetClientAccountId: '',
                      useCustomRates: false,
                      useFixedMarkupUsd: false,
                      fixedMarkupAmount: '',
                      customProviderRate: '',
                      customClientRate: '',
                      selectedMarkupRuleId: '',
                    }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select client</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {getClientOptionLabel(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Sell from account</label>
                  <select
                    value={createForm.sourceClientAccountId}
                    onChange={(e) => {
                      const nextSourceId = e.target.value;
                      const nextSource = clientAccounts.find((a) => a.id === nextSourceId);
                      setCreateForm((f) => {
                        const currentTarget = clientAccounts.find((a) => a.id === f.targetClientAccountId);
                        const targetStillOk = !!(
                          nextSource
                          && currentTarget
                          && currentTarget.id !== nextSource.id
                          && currentTarget.providerId === nextSource.providerId
                          && resolveAccountCurrency(currentTarget.currencyId) !== resolveAccountCurrency(nextSource.currencyId)
                        );
                        return {
                          ...f,
                          sourceClientAccountId: nextSourceId,
                          targetClientAccountId: targetStillOk ? f.targetClientAccountId : '',
                          useCustomRates: false,
                          useFixedMarkupUsd: false,
                          fixedMarkupAmount: '',
                          customProviderRate: '',
                          customClientRate: '',
                          selectedMarkupRuleId: '',
                        };
                      });
                      setQuote(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select source account</option>
                    {activeClientAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {formatFxAccountOption(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Buy to account</label>
                  <select
                    value={createForm.targetClientAccountId}
                    onChange={(e) => {
                      setCreateForm((f) => ({
                        ...f,
                        targetClientAccountId: e.target.value,
                        useCustomRates: false,
                        useFixedMarkupUsd: false,
                        fixedMarkupAmount: '',
                        customProviderRate: '',
                        customClientRate: '',
                        selectedMarkupRuleId: '',
                      }));
                      setQuote(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select target account</option>
                    {compatibleTargetAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {formatFxAccountOption(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs text-gray-600">Amount</label>
                    <button
                      type="button"
                      disabled={!selectedSourceAccount || Number(selectedSourceAccount.availableBalance ?? 0) <= 0}
                      onClick={() => {
                        const bal = Number(selectedSourceAccount?.availableBalance ?? 0);
                        if (!Number.isFinite(bal) || bal <= 0) return;
                        setCreateForm((f) => ({
                          ...f,
                          amount: bal.toFixed(2),
                          amountSide: 'from',
                        }));
                        setQuote(null);
                      }}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Full balance
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={createForm.amount}
                    onChange={(e) => {
                      setCreateForm((f) => ({ ...f, amount: e.target.value }));
                      setQuote(null);
                    }}
                    placeholder="Enter amount"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Amount side</label>
                  <select
                    value={createForm.amountSide}
                    onChange={(e) => {
                      setCreateForm((f) => ({ ...f, amountSide: e.target.value as 'from' | 'to' }));
                      setQuote(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="from">Amount in source currency</option>
                    <option value="to">Amount in target currency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Payment date</label>
                  <EnglishDatePicker
                    value={createForm.paymentDate}
                    onChange={(value) => setCreateForm((f) => ({ ...f, paymentDate: value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Markup rule (optional)</label>
                  <select
                    value={markupSelectValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '__CUSTOM__') {
                        setCreateForm((f) => ({
                          ...f,
                          useCustomRates: true,
                          useFixedMarkupUsd: false,
                          fixedMarkupAmount: '',
                          selectedMarkupRuleId: '',
                        }));
                      } else if (value === '__FIXED_USD__') {
                        setCreateForm((f) => ({
                          ...f,
                          useCustomRates: false,
                          useFixedMarkupUsd: true,
                          customClientRate: '',
                          selectedMarkupRuleId: '',
                        }));
                      } else {
                        const selectedRule = value ? availableMarkupRules.find((r) => r.id === value) : null;
                        const fixedRuleSelected = (selectedRule?.markupType || '').trim().toUpperCase() === 'FIXED';
                        setCreateForm((f) => ({
                          ...f,
                          useCustomRates: false,
                          useFixedMarkupUsd: false,
                          fixedMarkupAmount: fixedRuleSelected
                            ? String(selectedRule?.markupValue ?? '')
                            : '',
                          customProviderRate: '',
                          customClientRate: fixedRuleSelected ? '' : f.customClientRate,
                          selectedMarkupRuleId: value,
                        }));
                      }
                      setQuote(null);
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={loadingMarkupRules || !selectedSourceAccount || !selectedTargetAccount}
                  >
                    <option value="">{loadingMarkupRules ? 'Loading rules...' : 'Auto active rule'}</option>
                    <option value="__FIXED_USD__">Fixed markup in USD</option>
                    <option value="__CUSTOM__">Custom</option>
                    {availableMarkupRules.map((r) => (
                      <option key={r.id} value={r.id}>
                        {`${r.markupType} ${r.markupValue}${r.markupType === 'PERCENTAGE' ? '%' : ''} · ${r.sourceCurrency || '*'} / ${r.targetCurrency || '*'}`}
                      </option>
                    ))}
                  </select>
                </div>
                {isFixedMarkupMode && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Markup amount ({sourceCurrencyLabel})
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={createForm.fixedMarkupAmount}
                      onChange={(e) => {
                        setCreateForm((f) => ({ ...f, fixedMarkupAmount: e.target.value }));
                        setQuote(null);
                      }}
                      placeholder="e.g. 63.62"
                      disabled={!createForm.useFixedMarkupUsd && selectedMarkupRuleType === 'FIXED'}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Provider converts Amount − Markup, client receives that EUR result. Client rate is auto-calculated.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {createForm.useFixedMarkupUsd ? 'Provider rate' : 'Custom provider rate (optional)'}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={createForm.customProviderRate}
                    onChange={(e) => {
                      setCreateForm((f) => ({ ...f, customProviderRate: e.target.value }));
                      setQuote(null);
                    }}
                    placeholder="e.g. 1.1000000000"
                    disabled={!allowProviderRateInput}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Custom client rate (optional)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={createForm.customClientRate}
                    onChange={(e) => {
                      setCreateForm((f) => ({ ...f, customClientRate: e.target.value }));
                      setQuote(null);
                    }}
                    placeholder={isFixedMarkupMode ? 'Auto-calculated in Fixed mode' : 'e.g. 1.0845000000'}
                    disabled={!createForm.useCustomRates || isFixedMarkupMode}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
              </div>

              {(selectedSourceAccount || selectedTargetAccount) && (
                <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                  {selectedSourceAccount && (
                    <div>Sell: {formatFxAccountOption(selectedSourceAccount)}</div>
                  )}
                  {selectedTargetAccount && (
                    <div>Buy: {formatFxAccountOption(selectedTargetAccount)}</div>
                  )}
                  {selectedSourceAccount && compatibleTargetAccounts.length === 0 && (
                    <div className="text-amber-700">
                      No matching buy account for this provider/currency. FX needs another currency with the same provider.
                    </div>
                  )}
                </div>
              )}

              {(previewLoading || livePreview) && !quote && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs uppercase tracking-wide text-sky-700 font-semibold">Live rate</p>
                    {previewLoading && <span className="text-xs text-sky-600">Updating...</span>}
                  </div>
                  {livePreview && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-lg border border-sky-200 bg-white p-3">
                          <p className="text-xs text-gray-500 mb-1">You sell</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {livePreview.fromAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                            {resolveCurrencyCode(livePreview.fromCurrency, currencyCodeById) ?? livePreview.fromCurrency}
                          </p>
                        </div>
                        <div className="rounded-lg border border-sky-200 bg-white p-3">
                          <p className="text-xs text-gray-500 mb-1">You receive</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {livePreview.toAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                            {resolveCurrencyCode(livePreview.toCurrency, currencyCodeById) ?? livePreview.toCurrency}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-600">Current rate</span>
                        <span className="font-semibold text-gray-900 font-mono">
                          1 {livePreview.fromCurrency} = {formatRate(livePreview.clientRate)} {livePreview.toCurrency}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {livePreview.rateSource === 'PYVIO' ? 'Pyvio live' : livePreview.rateSource === 'MANUAL' ? 'Manual' : 'Internal tariff'}
                        {livePreview.markupType === 'PERCENTAGE' && livePreview.markupValue > 0 ? ` · markup ${livePreview.markupValue}%` : ''}
                        {' · '}
                        Refreshed at {new Date(livePreview.quotedAt).toLocaleTimeString()}
                      </div>
                    </>
                  )}
                </div>
              )}

              {quote && (
                <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4 text-sm">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">FX Quote</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Quote expires: {new Date(quote.quoteExpiresAt).toLocaleString('en-GB')}
                      </p>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                      Ready
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <div className="rounded-lg border border-indigo-200 bg-white p-3">
                      <p className="text-xs text-gray-500 mb-1">You sell</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {quote.fromAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {quoteFromCurrency}
                      </p>
                    </div>
                    <div className="rounded-lg border border-indigo-200 bg-white p-3">
                      <p className="text-xs text-gray-500 mb-1">You receive</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {quote.toAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {quoteToCurrency}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-600">Exchange rate</span>
                      <span className="font-semibold text-gray-900 text-right">
                        1 {quoteFromCurrency} = {formatRate(quote.clientRate)} {quoteToCurrency}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-gray-600">Provider / markup</span>
                      <span className="font-semibold text-gray-900 text-right">
                        {quoteProviderMarkupLabel}
                      </span>
                    </div>
                    {quoteIsFixedMarkup ? (
                      <>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600">Provider converts</span>
                          <span className="font-semibold text-gray-900 text-right">
                            {quoteProviderSourceAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {quoteFromCurrency}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600">Profit (fixed markup)</span>
                          <span className="font-semibold text-gray-900 text-right">
                            {quote.markupValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {quoteFromCurrency}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600">Rate difference</span>
                          <span className="font-semibold text-gray-900 text-right">
                            {(quote.providerRate - quote.clientRate).toFixed(6)} ({quoteFromCurrency}/{quoteToCurrency})
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-gray-600">Amount difference</span>
                          <span className="font-semibold text-gray-900 text-right">
                            {((quoteProviderSourceAmount * quote.providerRate) - quote.toAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {quoteToCurrency}
                          </span>
                        </div>
                      </>
                    )}
                    {quote.appliedMarkupRuleId && (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-600">Applied markup rule</span>
                        <span className="font-semibold text-gray-900 text-right break-all">
                          {appliedMarkupRule
                            ? `${appliedMarkupRule.markupType} ${appliedMarkupRule.markupValue}${appliedMarkupRule.markupType === 'PERCENTAGE' ? '%' : ''}`
                            : quote.appliedMarkupRuleId}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {actionError && (
                <p className="text-sm text-red-500">{actionError}</p>
              )}
            </div>
            <div className="flex gap-3 p-6 pt-0">
              <button
                type="button"
                onClick={handleGetQuote}
                disabled={quoteLoading || createLoading}
                className="flex-1 bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold py-2 rounded-lg text-sm transition-colors disabled:opacity-60"
              >
                {quoteLoading ? 'Getting quote...' : 'Get quote'}
              </button>
              <button
                type="button"
                onClick={handleCreateOrder}
                disabled={createLoading || !quote}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg text-sm transition-colors disabled:opacity-60"
              >
                {createLoading ? 'Creating...' : 'Create order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">FX Order</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              {[
                ['ID', selected.id],
                ['Client', selected.clientName || selected.userId],
                ['From', `${selected.fromAmount} ${displayFxCurrency(selected.fromCurrency)}`],
                ['To', `${selected.toAmount} ${displayFxCurrency(selected.toCurrency)}`],
                ['Client Rate', formatRate(selected.clientRate)],
                ['Provider Rate', formatRate(selected.providerRate)],
                ['Executed Provider Rate', selected.finalProviderRate != null ? formatRate(selected.finalProviderRate) : '—'],
                ['Rate source', selected.rateSource === 'PYVIO'
                  ? 'Pyvio live'
                  : selected.rateSource === 'MANUAL'
                    ? 'Manual'
                    : selected.rateSource === 'PROVIDER'
                      ? 'Internal tariff'
                      : selected.rateSource || '—'],
                ['Executed Client Rate', selected.finalExecutedRate != null ? formatRate(selected.finalExecutedRate) : '—'],
                ['Profit', selected.profitAmount != null && selected.profitAmount > 0
                  ? `${selected.profitAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${displayFxCurrency(selected.profitCurrency || selected.fromCurrency)}`
                  : '—'],
                ['Markup', ['FIXED', 'FIXED_AMOUNT'].includes((selected.markupType || '').toUpperCase())
                  ? `${selected.markupValue} ${displayFxCurrency(selected.fromCurrency)} (${selected.markupType || '—'})`
                  : `${selected.markupValue}% (${selected.markupType || '—'})`],
                ['Status', selected.status],
                ['Provider', selected.providerName || '—'],
                ['Execution Mode', selected.executionMode || '—'],
                ['Reference', selected.reference || '—'],
                ['Cancel Reason', selected.cancelReason || '—'],
                ['Reject Reason', selected.rejectedReason || '—'],
                ['Payment date', formatFxDate(selected.paymentDate, selected.createdAt)],
                ['Created', formatFxDateTime(selected.createdAt)],
                ['Executed', selected.executedAt ? formatFxDateTime(selected.executedAt) : '—'],
              ].map(([k, v]) => (
                <div key={k as string} className="flex justify-between">
                  <span className="text-gray-500">{k}</span>
                  <span className={`font-medium text-gray-900 text-right max-w-[60%] break-all ${k === 'Client Rate' || k === 'Provider Rate' || k === 'Executed Client Rate' ? 'font-mono' : ''}`}>{v}</span>
                </div>
              ))}
            </div>
            {actionError && <p className="px-6 pb-2 text-sm text-red-500">{actionError}</p>}
            {selected.status === 'Awaiting Pyvio Confirmation' && (
              <p className="mx-6 mb-6 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800">
                Pyvio accepted this FX order. Client balances will be updated after Pyvio sends final confirmation.
              </p>
            )}
            {(selected.status === 'Awaiting Execution') && (
              <>
                {showExecuteForm ? (
                  <div className="px-6 pb-6 space-y-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                      <strong>Indicative rates (at quote):</strong> Provider&nbsp;
                      <span className="font-mono font-semibold">{formatRate(selected.providerRate)}</span>
                      &nbsp;·&nbsp;Client&nbsp;
                      <span className="font-mono font-semibold">{formatRate(selected.clientRate)}</span>
                      <br />
                      {selected.rateSource === 'PYVIO'
                        ? 'This provider is connected to Pyvio. Execute locks a live rate (60s) and sends the FX order to the API. Markup stays from internal rules.'
                        : 'This provider has no API integration. Execute uses the rate you enter here, or the internal daily tariff from quote.'}
                      <br />
                      If real provider rate &lt; indicative, the order will be <strong>auto-cancelled</strong> and funds released.
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Real Provider Rate <span className="text-red-500">*</span>
                        <span className="text-gray-400 ml-1">(prefilled from indicative, editable)</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={execProviderRate}
                        onChange={e => setExecProviderRate(e.target.value)}
                        placeholder={formatRateInput(selected.providerRate)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Real Client Rate
                        <span className="text-gray-400 ml-1">(prefilled from indicative, editable)</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={execClientRate}
                        onChange={e => setExecClientRate(e.target.value)}
                        placeholder={formatRateInput(selected.clientRate)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Provider Trade ID <span className="text-gray-400">(optional)</span></label>
                      <input
                        type="text"
                        value={execTradeId}
                        onChange={e => setExecTradeId(e.target.value)}
                        placeholder="e.g. TRD-20260509-001"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={handleExecuteSubmit}
                        disabled={actionLoading}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg text-sm transition-colors disabled:opacity-60"
                      >
                        {actionLoading ? 'Processing...' : 'Confirm Execute'}
                      </button>
                      <button
                        onClick={() => { setShowExecuteForm(false); setActionError(''); }}
                        disabled={actionLoading}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 p-6 pt-0">
                    <button
                      onClick={openExecuteForm}
                      disabled={actionLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg text-sm transition-colors disabled:opacity-60"
                    >
                      Execute
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={actionLoading}
                      className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 font-semibold py-2 rounded-lg text-sm transition-colors border border-red-200 disabled:opacity-60"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Rates Tab ────────────────────────────────────────────────────────────────
function RatesTab({ providers, currencyCodeById }: { providers: Provider[]; currencyCodeById: Map<string, string> }) {
  const [rates, setRates] = useState<IndicativeDailyRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const hktDateTimeLocal = () => {
    const now = new Date();
    const hktNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong' }));
    return `${hktNow.getFullYear()}-${String(hktNow.getMonth() + 1).padStart(2, '0')}-${String(hktNow.getDate()).padStart(2, '0')}T${String(hktNow.getHours()).padStart(2, '0')}:${String(hktNow.getMinutes()).padStart(2, '0')}`;
  };
  const toUtcIso = (localHktDateTime: string) => {
    if (!localHktDateTime) return '';
    const asUtc = new Date(`${localHktDateTime}:00+08:00`);
    return asUtc.toISOString();
  };
  const toHktInputValue = (iso?: string) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const hkt = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong' }));
    return `${hkt.getFullYear()}-${String(hkt.getMonth() + 1).padStart(2, '0')}-${String(hkt.getDate()).padStart(2, '0')}T${String(hkt.getHours()).padStart(2, '0')}:${String(hkt.getMinutes()).padStart(2, '0')}`;
  };
  const formatHkt = (iso?: string) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
      timeZone: 'Asia/Hong_Kong',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };
  const currentHktDateTimeLabel = formatHkt(new Date().toISOString());
  const [editingRateId, setEditingRateId] = useState<string | null>(null);
  const [form, setForm] = useState({
    providerId: '', sourceCurrency: '', targetCurrency: '',
    indicativeBaseRate: '', validFrom: hktDateTimeLocal(), validTo: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === form.providerId),
    [providers, form.providerId]
  );
  const providerCurrencies = useMemo(() => {
    const fromProvider = selectedProvider?.currencies ?? [];
    const unique = new Map<string, string>();
    for (const c of fromProvider) {
      const code =
        resolveCurrencyCode(c.currencyId, currencyCodeById)
        ?? resolveCurrencyCode(c.currency, currencyCodeById);
      if (!code) continue;
      if (!unique.has(code)) unique.set(code, code);
    }
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }));
  }, [selectedProvider, currencyCodeById]);

  const load = async () => {
    setLoading(true);
    try { const r = await fxAdminApi.getRates(); setRates(r.data); } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ providerId: '', sourceCurrency: '', targetCurrency: '', indicativeBaseRate: '', validFrom: hktDateTimeLocal(), validTo: '' });
    setEditingRateId(null);
  };

  const handleSave = async () => {
    setError('');
    const baseRate = parseFloat(form.indicativeBaseRate);
    const sourceCurrency = form.sourceCurrency.trim().toUpperCase();
    const targetCurrency = form.targetCurrency.trim().toUpperCase();
    if (!form.providerId) {
      setError('Select provider.');
      return;
    }
    if (!sourceCurrency || !targetCurrency) {
      setError('Select source and target currencies.');
      return;
    }
    if (sourceCurrency === targetCurrency) {
      setError('Source and target currencies must be different.');
      return;
    }
    if (!Number.isFinite(baseRate) || baseRate <= 0) {
      setError('Base rate must be greater than 0.');
      return;
    }
    const validFromUtc = toUtcIso(form.validFrom);
    const validToUtc = form.validTo ? toUtcIso(form.validTo) : undefined;
    if (!validFromUtc) {
      setError('Valid from is required.');
      return;
    }
    if (validToUtc && new Date(validToUtc).getTime() <= new Date(validFromUtc).getTime()) {
      setError('Valid to must be later than Valid from.');
      return;
    }

    setSaving(true);
    try {
      await fxAdminApi.upsertRate({
        id: editingRateId || undefined,
        providerId: form.providerId,
        sourceCurrency,
        targetCurrency,
        indicativeBaseRate: baseRate,
        indicativeClientRate: baseRate,
        validFrom: validFromUtc,
        validTo: validToUtc,
      });
      setShowForm(false);
      resetForm();
      await load();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rate: IndicativeDailyRate) => {
    setEditingRateId(rate.id);
    setForm({
      providerId: rate.providerId,
      sourceCurrency: rate.sourceCurrency,
      targetCurrency: rate.targetCurrency,
      indicativeBaseRate: String(rate.indicativeBaseRate),
      validFrom: toHktInputValue(rate.validFrom),
      validTo: toHktInputValue(rate.validTo),
    });
    setError('');
    setShowForm(true);
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm('Deactivate this rate?')) return;
    try { await fxAdminApi.deactivateRate(id); load(); } catch (e) { alert(getErrorMessage(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-medium text-gray-700">Indicative Daily Rates</h2>
        <span className="text-xs text-gray-500">Current time (HKT): {currentHktDateTimeLabel}</span>
        <button onClick={() => {
          if (showForm) {
            setShowForm(false);
            setError('');
            resetForm();
            return;
          }
          setShowForm(true);
          setError('');
        }}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          + Add / Update Rate
        </button>
      </div>

      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Provider</label>
            <select
              value={form.providerId}
              onChange={e => setForm(f => ({ ...f, providerId: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!!editingRateId}
              required
            >
              <option value="">Select provider</option>
              {providers.map(p => (
                <option key={p.id} value={p.id!}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Source Currency</label>
            <select
              value={form.sourceCurrency}
              onChange={e => setForm(f => ({ ...f, sourceCurrency: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!form.providerId || !!editingRateId}
            >
              <option value="">{form.providerId ? 'Select source currency' : 'Select provider first'}</option>
              {providerCurrencies.map(c => (
                <option key={`src-${c.value}`} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Target Currency</label>
            <select
              value={form.targetCurrency}
              onChange={e => setForm(f => ({ ...f, targetCurrency: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!form.providerId || !!editingRateId}
            >
              <option value="">{form.providerId ? 'Select target currency' : 'Select provider first'}</option>
              {providerCurrencies
                .filter(c => c.value !== form.sourceCurrency)
                .map(c => (
                  <option key={`tgt-${c.value}`} value={c.value}>{c.label}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Base Rate (Provider)</label>
            <input
              type="number"
              step="0.000001"
              min="0"
              value={form.indicativeBaseRate}
              onChange={e => setForm(f => ({ ...f, indicativeBaseRate: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Valid from (HKT)</label>
            <input
              type="datetime-local"
              value={form.validFrom}
              onChange={e => setForm(f => ({ ...f, validFrom: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Valid to (HKT, optional)</label>
            <input
              type="datetime-local"
              value={form.validTo}
              onChange={e => setForm(f => ({ ...f, validTo: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {error && <p className="col-span-2 text-sm text-red-500">{error}</p>}
          <div className="col-span-2 flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60">
              {saving ? 'Saving...' : editingRateId ? 'Update Rate' : 'Save Rate'}
            </button>
            <button onClick={() => { setShowForm(false); setError(''); resetForm(); }} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Provider', 'Pair', 'Base Rate', 'Valid from (HKT)', 'Valid to (HKT)', 'Active', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rates.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-700">{r.providerName || r.providerId.slice(0, 8)}</td>
                  <td className="px-4 py-3 font-medium">{r.sourceCurrency}/{r.targetCurrency}</td>
                  <td className="px-4 py-3">{r.indicativeBaseRate}</td>
                  <td className="px-4 py-3 text-gray-500">{formatHkt(r.validFrom)}</td>
                  <td className="px-4 py-3 text-gray-500">{formatHkt(r.validTo)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => handleEdit(r)} className="text-blue-600 hover:underline text-xs">Edit</button>
                      {r.isActive && (
                        <button onClick={() => handleDeactivate(r.id)} className="text-red-500 hover:underline text-xs">Deactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rates.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No rates configured</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Markup Rules Tab ─────────────────────────────────────────────────────────
function MarkupTab({ providers, currencyCodeById }: { providers: Provider[]; currencyCodeById: Map<string, string> }) {
  const [rules, setRules] = useState<FxMarkupRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    providerId: '', sourceCurrency: '', targetCurrency: '',
    markupType: 'PERCENTAGE', markupValue: '', minAmount: '', maxAmount: '',
    effectiveFrom: new Date().toISOString().slice(0, 16), effectiveTo: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === form.providerId),
    [providers, form.providerId]
  );
  const providerCurrencies = useMemo(() => {
    const fromProvider = selectedProvider?.currencies ?? [];
    const unique = new Map<string, string>();
    for (const c of fromProvider) {
      const code =
        resolveCurrencyCode(c.currencyId, currencyCodeById)
        ?? resolveCurrencyCode(c.currency, currencyCodeById);
      if (!code) continue;
      if (!unique.has(code)) unique.set(code, code);
    }
    return Array.from(unique.entries()).map(([value, label]) => ({ value, label }));
  }, [selectedProvider, currencyCodeById]);

  const load = async () => {
    setLoading(true);
    try { const r = await fxAdminApi.getMarkupRules(); setRules(r.data); } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const sourceCurrency = form.sourceCurrency.trim().toUpperCase();
      const targetCurrency = form.targetCurrency.trim().toUpperCase();
      if (!form.providerId) {
        setError('Select provider.');
        return;
      }
      if ((sourceCurrency && !targetCurrency) || (!sourceCurrency && targetCurrency)) {
        setError('Set both source and target currencies, or leave both blank for Any.');
        return;
      }
      if (sourceCurrency && targetCurrency && sourceCurrency === targetCurrency) {
        setError('Source and target currencies must be different.');
        return;
      }

      await fxAdminApi.createMarkupRule({
        providerId: form.providerId,
        sourceCurrency: sourceCurrency || undefined,
        targetCurrency: targetCurrency || undefined,
        markupType: form.markupType,
        markupValue: parseFloat(form.markupValue),
        minAmount: form.minAmount ? parseFloat(form.minAmount) : undefined,
        maxAmount: form.maxAmount ? parseFloat(form.maxAmount) : undefined,
        effectiveFrom: new Date(form.effectiveFrom).toISOString(),
        effectiveTo: form.effectiveTo ? new Date(form.effectiveTo).toISOString() : undefined,
      });
      setShowForm(false);
      setForm({
        providerId: '', sourceCurrency: '', targetCurrency: '', markupType: 'PERCENTAGE',
        markupValue: '', minAmount: '', maxAmount: '',
        effectiveFrom: new Date().toISOString().slice(0, 16), effectiveTo: ''
      });
      load();
    } catch (e) { setError(getErrorMessage(e)); }
    setSaving(false);
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm('Deactivate this markup rule?')) return;
    try { await fxAdminApi.deactivateMarkupRule(id); load(); } catch (e) { alert(getErrorMessage(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-medium text-gray-700">FX Markup Rules</h2>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          + Add Rule
        </button>
      </div>

      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Provider</label>
            <select
              value={form.providerId}
              onChange={e => setForm(f => ({ ...f, providerId: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select provider</option>
              {providers.map(p => (
                <option key={p.id} value={p.id!}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Source Currency (blank = any)</label>
            <select
              value={form.sourceCurrency}
              onChange={e => setForm(f => ({ ...f, sourceCurrency: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!form.providerId}
            >
              <option value="">{form.providerId ? 'Any' : 'Select provider first'}</option>
              {providerCurrencies.map(c => (
                <option key={`markup-src-${c.value}`} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Target Currency (blank = any)</label>
            <select
              value={form.targetCurrency}
              onChange={e => setForm(f => ({ ...f, targetCurrency: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!form.providerId}
            >
              <option value="">{form.providerId ? 'Any' : 'Select provider first'}</option>
              {providerCurrencies
                .filter(c => c.value !== form.sourceCurrency)
                .map(c => (
                  <option key={`markup-tgt-${c.value}`} value={c.value}>{c.label}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Markup Value</label>
            <input
              type="number"
              step="0.0001"
              value={form.markupValue}
              onChange={e => setForm(f => ({ ...f, markupValue: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Min Amount (blank = no min)</label>
            <input
              type="number"
              step="0.0001"
              value={form.minAmount}
              onChange={e => setForm(f => ({ ...f, minAmount: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Max Amount (blank = no max)</label>
            <input
              type="number"
              step="0.0001"
              value={form.maxAmount}
              onChange={e => setForm(f => ({ ...f, maxAmount: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Effective From</label>
            <input
              type="datetime-local"
              value={form.effectiveFrom}
              onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Effective To (blank = open-ended)</label>
            <input
              type="datetime-local"
              value={form.effectiveTo}
              onChange={e => setForm(f => ({ ...f, effectiveTo: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Markup Type</label>
            <select value={form.markupType} onChange={e => setForm(f => ({ ...f, markupType: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="PERCENTAGE">PERCENTAGE (%)</option>
              <option value="FIXED">FIXED (absolute)</option>
            </select>
          </div>
          {error && <p className="col-span-2 text-sm text-red-500">{error}</p>}
          <div className="col-span-2 flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60">
              {saving ? 'Saving...' : 'Create Rule'}
            </button>
            <button onClick={() => setShowForm(false)} className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Provider', 'Pair', 'Type', 'Value', 'Amount Range', 'From', 'To', 'Active', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-700">{r.providerName || r.providerId.slice(0, 8)}</td>
                  <td className="px-4 py-3">{r.sourceCurrency || '*'}/{r.targetCurrency || '*'}</td>
                  <td className="px-4 py-3 text-gray-600">{r.markupType}</td>
                  <td className="px-4 py-3 font-medium">{r.markupValue}{r.markupType === 'PERCENTAGE' ? '%' : ''}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {(r.minAmount ?? null) !== null ? r.minAmount : '*'} - {(r.maxAmount ?? null) !== null ? r.maxAmount : '*'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{new Date(r.effectiveFrom).toLocaleDateString('en-GB')}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{r.effectiveTo ? new Date(r.effectiveTo).toLocaleDateString('en-GB') : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {r.isActive && (
                      <button onClick={() => handleDeactivate(r.id)} className="text-red-500 hover:underline text-xs">Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No markup rules configured</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function FxPage() {
  const [tab, setTab] = useState<Tab>('orders');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyDto[]>([]);
  const currencyCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of currencies) {
      if (!c?.id || !c?.code) continue;
      map.set(c.id.toUpperCase(), c.code.toUpperCase());
    }
    return map;
  }, [currencies]);

  useEffect(() => {
    providersApi.getAll().then(setProviders).catch(() => {});
    adminApi.getCurrencies().then((r) => setCurrencies(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">FX Management</h1>
        <p className="text-gray-600 text-sm">Manage foreign exchange orders, rates, and markup</p>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {(['orders', 'rates', 'markup'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'markup' ? 'Markup Rules' : t === 'rates' ? 'Daily Rates' : 'Orders'}
          </button>
        ))}
      </div>

      {tab === 'orders' && <OrdersTab />}
      {tab === 'rates' && <RatesTab providers={providers} currencyCodeById={currencyCodeById} />}
      {tab === 'markup' && <MarkupTab providers={providers} currencyCodeById={currencyCodeById} />}
    </div>
  );
}

export default FxPage;
